import ImmutableMap from './ImmutableMap';

/**
 * A custom set class, allowing more value types than builtin ES6 Set objects.
 *
 * Allows any value type which json-stable-stringify can process. Values should not be mutated after adding
 * to the set. Values with the same stingify output are considered equal.
 * Values should be uniquely representable with their JSON representations. Classes that wish to be used
 * as values in ImmutableSet can implement toJSON if json-stable-stringify is not sufficient.
 *
 * The set is immutable, meaning methods like .add and .delete do not mutate the base object
 * but rather return clones of the base object with the relevant value added/removed.
 *
 * Example:
 *
 * interface Point {
 *  x: number;
 *  y: number
 * }
 *
 * const points = new ImmutableSet<Point>().add({x: 3, y: 4});
 * points.has({x: 3, y: 4});          // outputs true
 * points.delete({y: 4, x: 3}); // outputs ImmutableSet with no entries
 * points.add({x: 3});          // does not typecheck (missing y from interface)
 *
 * Also note that an object satisfying Point but with extra fields will be treated as a unique value.
 * Discussion: https://github.com/benchling/aurelia/pull/13636#issuecomment-649791776
 *
 * Other useful examples can be found in './__tests__/ImmutableSet-test.ts'
 */
export default class ImmutableSet<T> {
  private map: ImmutableMap<T, true>;

  constructor(values?: Array<T>) {
    this.map = new ImmutableMap<T, true>((values ?? []).map((value) => [value, true]));
  }

  has(value: T): boolean {
    return this.map.has(value);
  }

  size(): number {
    return this.map.size();
  }

  add(value: T): ImmutableSet<T> {
    const newSet = new ImmutableSet<T>();
    newSet.map = this.map.set(value, true);
    return newSet;
  }

  delete(value: T): ImmutableSet<T> {
    const newSet = new ImmutableSet<T>();
    newSet.map = this.map.delete(value);
    return newSet;
  }

  [Symbol.iterator](): Iterator<T> {
    return this.map.keys();
  }
}
